import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    ShoppingBag,
    Heart,
    Mail,
    MailWarning,
    Send,
    CheckCircle2,
    Clock,
    XCircle,
    RefreshCcw,
    LogOut,
    Download,
    MailQuestion,
    BellOff,
} from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/common/SEO";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

const STATUS_META = {
    pending: { label: "Pendiente", icon: Clock, color: "text-[var(--warning)]" },
    paid: { label: "Aportó", icon: CheckCircle2, color: "text-[var(--warning)]" },
    free: { label: "Gratis", icon: Heart, color: "text-[var(--paper)]" },
    failed: { label: "Fallido", icon: XCircle, color: "text-[var(--danger)]" },
    refunded: { label: "Reembolsado", icon: RefreshCcw, color: "text-[var(--ink-muted)]" },
};

export default function AdminOrders() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [list, st] = await Promise.all([
                api.get("/admin/orders?limit=200"),
                api.get("/admin/orders/stats"),
            ]);
            setOrders(list.data.items || []);
            setStats(st.data);
        } catch {
            toast.error("No se pudieron cargar los pedidos.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        reload();
    }, [reload]);

    async function handleLogout() {
        await logout();
        navigate("/login");
    }

    async function markPaid(id) {
        if (!confirm("¿Marcar este pedido como pagado manualmente?")) return;
        try {
            await api.post(`/admin/orders/${id}/mark-paid`);
            toast.success("Pedido actualizado.");
            await reload();
        } catch {
            toast.error("No se pudo actualizar.");
        }
    }

    async function resendEmail(id, email) {
        if (!confirm(`¿Reenviar el email de confirmación a ${email}?`)) return;
        try {
            const { data } = await api.post(`/admin/orders/${id}/resend-email`);
            toast.success(`Email enviado a ${data.to} (id: ${(data.email_id || "").slice(0, 10)}…)`);
            await reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo reenviar el email.");
        }
    }

    async function sendFollowup(id, email) {
        if (
            !confirm(
                `¿Enviar el email de follow-up (+3 días) a ${email} ahora?\n\nSi ya se envió antes, se reenviará.`,
            )
        )
            return;
        try {
            const { data } = await api.post(`/admin/orders/${id}/send-followup`);
            toast.success(
                `Follow-up enviado a ${data.to} (id: ${(data.email_id || "").slice(0, 10)}…)`,
            );
            await reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo enviar el follow-up.");
        }
    }

    async function runFollowupQueue() {
        if (!confirm("¿Correr la cola de follow-ups ahora? (procesa órdenes >3 días sin enviar)")) return;
        try {
            const { data } = await api.post("/admin/follow-up/run-now");
            toast.success(
                `Cola procesada · revisadas: ${data.checked} · enviadas: ${data.sent}`,
            );
            await reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo correr la cola.");
        }
    }

    return (
        <>
            <SEO title="Admin · Pedidos" description="Gestión de pedidos del libro" path="/admin/pedidos" />
            <section className="py-12 md:py-16" data-testid="admin-orders">
                <div className="container-x">
                    <div className="flex flex-wrap justify-between items-start gap-4 mb-10 pb-8 border-b border-[var(--line)]">
                        <div>
                            <Link
                                to="/admin"
                                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] hover:text-[var(--danger)] mb-4"
                                data-testid="admin-back-to-hub"
                            >
                                <ArrowLeft className="w-3 h-3" />
                                Volver al panel
                            </Link>
                            <p className="eyebrow mb-3">Departamento · Pedidos del libro</p>
                            <h1 className="font-display text-4xl md:text-6xl text-[var(--paper-2)] leading-[0.95]">
                                Órdenes & <span className="text-[var(--danger)]">descargas</span>
                            </h1>
                            <p className="font-mono text-xs text-[var(--ink-muted)] mt-2">
                                Sesión: {user?.email}
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <button
                                onClick={runFollowupQueue}
                                className="btn-brutal btn-brutal-ghost"
                                data-testid="orders-run-followup-queue"
                                title="Procesar cola de follow-ups (órdenes >3 días)"
                            >
                                <MailQuestion className="w-4 h-4" />
                                Cola follow-up
                            </button>
                            <button
                                onClick={reload}
                                className="btn-brutal btn-brutal-ghost"
                                disabled={loading}
                                data-testid="orders-reload"
                            >
                                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                                Recargar
                            </button>
                            <button
                                onClick={handleLogout}
                                className="btn-brutal btn-brutal-ghost"
                            >
                                <LogOut className="w-4 h-4" />
                                Salir
                            </button>
                        </div>
                    </div>

                    {stats && (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12" data-testid="kpi-grid">
                            <Kpi icon={ShoppingBag} label="Órdenes totales" value={stats.total} color="text-[var(--paper-2)]" />
                            <Kpi icon={Heart} label="Descargas gratis" value={stats.free ?? 0} color="text-[var(--paper)]" />
                            <Kpi icon={CheckCircle2} label="Completadas" value={stats.completed ?? stats.paid} color="text-[var(--warning)]" />
                            <Kpi icon={Clock} label="Pendientes" value={stats.pending ?? 0} color="text-[var(--danger)]" />
                        </div>
                    )}

                    {stats && (
                        <div className="grid sm:grid-cols-3 gap-4 mb-12" data-testid="kpi-grid-followup">
                            <Kpi icon={MailQuestion} label="Follow-ups enviados" value={stats.followup_sent ?? 0} color="text-[var(--warning)]" />
                            <Kpi icon={BellOff} label="Opt-out" value={stats.followup_opted_out ?? 0} color="text-[var(--ink-muted)]" />
                            <Kpi icon={XCircle} label="Follow-ups fallidos" value={stats.followup_failed ?? 0} color="text-[var(--danger)]" />
                        </div>
                    )}

                    <div className="border border-[var(--line)] overflow-x-auto" data-testid="orders-table">
                        <table className="w-full">
                            <thead className="bg-[var(--paper-2)] text-black">
                                <tr className="font-mono text-[10px] uppercase tracking-[0.2em]">
                                    <th className="text-left p-3">Fecha</th>
                                    <th className="text-left p-3">Comprador</th>
                                    <th className="text-left p-3">Email</th>
                                    <th className="text-left p-3">País</th>
                                    <th className="text-right p-3">Monto</th>
                                    <th className="text-left p-3">Estado</th>
                                    <th className="text-left p-3 hidden lg:table-cell">Follow-up</th>
                                    <th className="text-right p-3">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map((o) => {
                                    const meta = STATUS_META[o.status] || STATUS_META.pending;
                                    const Icon = meta.icon;
                                    return (
                                        <tr
                                            key={o.id}
                                            className="border-t border-[var(--line)] hover:bg-[#1a1a1a]"
                                            data-testid={`order-row-${o.id}`}
                                        >
                                            <td className="p-3 font-mono text-xs text-[var(--ink-muted)] whitespace-nowrap">
                                                {(o.created_at || "").slice(0, 16).replace("T", " ")}
                                            </td>
                                            <td className="p-3 font-mono text-sm text-[var(--paper-2)]">
                                                {o.buyer?.name || "—"}
                                            </td>
                                            <td className="p-3 font-mono text-xs text-[var(--paper)] break-all">
                                                {o.buyer?.email || "—"}
                                            </td>
                                            <td className="p-3 font-mono text-xs text-[var(--paper)]">
                                                {o.buyer?.country || "—"}
                                            </td>
                                            <td className="p-3 font-mono text-sm text-right text-[var(--paper-2)]">
                                                {o.amount_label || (o.status === "free" || !o.amount ? "Gratis" : `$${(o.amount || 0).toLocaleString("es-CL")}`)}
                                            </td>
                                            <td className="p-3">
                                                <span className={`font-mono text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1.5 ${meta.color}`}>
                                                    <Icon className="w-3 h-3" />
                                                    {meta.label}
                                                </span>
                                            </td>
                                            <td className="p-3 hidden lg:table-cell">
                                                <FollowupBadge order={o} />
                                            </td>
                                            <td className="p-3 text-right">
                                                <div className="inline-flex items-center gap-2 justify-end">
                                                    {(o.status === "paid" || o.status === "free") && (
                                                        <button
                                                            onClick={() => resendEmail(o.id, o.buyer?.email)}
                                                            className="p-2 border border-[var(--paper)] text-[var(--paper)] hover:bg-[var(--paper)] hover:text-black"
                                                            data-testid={`resend-email-${o.id}`}
                                                            title={
                                                                o.email_sent_at
                                                                    ? `Reenviar email · último envío ${o.email_sent_at.slice(0, 16).replace("T", " ")}`
                                                                    : o.email_error
                                                                      ? `Reintentar envío · último error: ${o.email_error}`
                                                                      : "Enviar email de confirmación"
                                                            }
                                                        >
                                                            {o.email_error && !o.email_sent_at ? (
                                                                <MailWarning className="w-3.5 h-3.5 text-[var(--danger)]" />
                                                            ) : (
                                                                <Send className="w-3.5 h-3.5" />
                                                            )}
                                                        </button>
                                                    )}
                                                    {(o.status === "paid" || o.status === "free") &&
                                                        o.followup_status !== "opted_out" && (
                                                            <button
                                                                onClick={() =>
                                                                    sendFollowup(o.id, o.buyer?.email)
                                                                }
                                                                className="p-2 border border-[var(--warning)] text-[var(--warning)] hover:bg-[var(--warning)] hover:text-black"
                                                                data-testid={`send-followup-${o.id}`}
                                                                title={
                                                                    o.followup_sent_at
                                                                        ? `Reenviar follow-up · último envío ${o.followup_sent_at.slice(0, 16).replace("T", " ")}`
                                                                        : o.followup_error
                                                                          ? `Reintentar follow-up · error: ${o.followup_error}`
                                                                          : "Enviar follow-up (+3 días) ahora"
                                                                }
                                                            >
                                                                <MailQuestion className="w-3.5 h-3.5" />
                                                            </button>
                                                        )}
                                                    {o.status !== "paid" && o.status !== "free" && (
                                                        <button
                                                            onClick={() => markPaid(o.id)}
                                                            className="p-2 border border-[var(--warning)] text-[var(--warning)] hover:bg-[var(--warning)] hover:text-black"
                                                            data-testid={`mark-paid-${o.id}`}
                                                            title="Marcar como pagado"
                                                        >
                                                            <CheckCircle2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {orders.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="p-10 text-center font-mono text-sm text-[var(--ink-muted)]">
                                            {loading
                                                ? "Cargando pedidos…"
                                                : "Sin pedidos todavía. Las órdenes aparecen acá apenas alguien inicie la compra."}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-8 border border-dashed border-[var(--warning)] bg-[#0d0d0d] p-5 space-y-4">
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--warning)] mb-2 flex items-center gap-2">
                                <CheckCircle2 className="w-3 h-3" />
                                Modelo · Libro 100% gratis
                            </p>
                            <p className="font-mono text-xs text-[var(--paper)] leading-relaxed">
                                El libro es completamente gratuito. Todas las descargas se marcan
                                con status <em>free</em>, se confirman al instante y disparan el
                                email con el link. Se registra el evento{" "}
                                <code className="text-[var(--warning)]">order_paid</code> en
                                analytics. Aunque MercadoPago ya no interviene en la UI, el
                                backend mantiene el flujo por si se decide reactivar
                                aportes voluntarios en el futuro.
                            </p>
                        </div>
                        <div className="border-t border-dashed border-[var(--ink-dim)] pt-4">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--warning)] mb-2 flex items-center gap-2">
                                <MailQuestion className="w-3 h-3" />
                                Follow-up automático · +3 días
                            </p>
                            <p className="font-mono text-xs text-[var(--paper)] leading-relaxed">
                                Cada 30 min un job revisa órdenes <em>paid/free</em> con +3 días
                                desde la descarga y envía un email invitando a dejar un
                                testimonio + visitar SinAdicciones.org. Es idempotente y respeta
                                el opt-out del usuario. El botón <em>Cola follow-up</em> de
                                arriba corre el job manualmente (útil para testing). El icono
                                ⓘ en la fila gatilla el envío inmediato para una orden específica.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

function Kpi({ icon: Icon, label, value, color }) {
    return (
        <div className="border border-[var(--line)] bg-[#0d0d0d] p-6">
            <Icon className={`w-6 h-6 ${color} mb-4`} />
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] mb-2">
                {label}
            </p>
            <p className={`font-display text-5xl leading-none ${color}`}>{value}</p>
        </div>
    );
}

function FollowupBadge({ order }) {
    const status = order.followup_status;
    const sentAt = order.followup_sent_at;
    if (status === "opted_out") {
        return (
            <span
                className="font-mono text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1.5 text-[var(--ink-muted)]"
                data-testid={`followup-status-${order.id}`}
                title="El usuario se dio de baja del follow-up"
            >
                <BellOff className="w-3 h-3" />
                Opt-out
            </span>
        );
    }
    if (status === "sent" || sentAt) {
        return (
            <span
                className="font-mono text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1.5 text-[var(--warning)]"
                data-testid={`followup-status-${order.id}`}
                title={sentAt ? `Enviado ${sentAt.slice(0, 16).replace("T", " ")}` : "Enviado"}
            >
                <CheckCircle2 className="w-3 h-3" />
                Enviado
            </span>
        );
    }
    if (status === "failed") {
        return (
            <span
                className="font-mono text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1.5 text-[var(--danger)]"
                data-testid={`followup-status-${order.id}`}
                title={order.followup_error || "Falló el envío"}
            >
                <XCircle className="w-3 h-3" />
                Falló
            </span>
        );
    }
    if (order.status !== "paid" && order.status !== "free") {
        return <span className="text-[var(--ink-dim)] font-mono text-[10px]">—</span>;
    }
    return (
        <span
            className="font-mono text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1.5 text-[var(--ink-muted)]"
            data-testid={`followup-status-${order.id}`}
            title="Se enviará automáticamente al cumplir 3 días"
        >
            <Clock className="w-3 h-3" />
            Programado
        </span>
    );
}

